import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import clsx from "clsx";

import { Button } from "../components/common/Button";
import { Input } from "../components/common/Input";
import { Select } from "../components/common/Select";
import { Spinner } from "../components/common/Spinner";
import { ConfirmDialog } from "../components/common/ConfirmDialog";
import {
  getEncode, patchEncode, getEncodeCaps, parseCapsForChannel,
  getChannelTitles, patchChannelTitles, getNtp, patchNtp,
} from "../api/config";
import { getDevice } from "../api/devices";
import type { Device } from "../types";
import type {
  ChannelCaps, EncodeChannel, StreamFormat, StreamKind, VideoFormat,
} from "../types/encode";
import { MotionDetectTab } from "./deviceConfig/MotionDetectTab";
import { NetworkTab } from "./deviceConfig/NetworkTab";
import { SnapshotTab } from "./deviceConfig/SnapshotTab";
import { SmartEventsTab } from "./deviceConfig/SmartEventsTab";
import { StorageTab } from "./deviceConfig/StorageTab";
import { SystemInfoTab } from "./deviceConfig/SystemInfoTab";
import { TimeTab } from "./deviceConfig/TimeTab";

const TABS = [
  "Red",
  "Canales",
  "Calidad video",
  "Snapshot",
  "Detección movimiento",
  "Eventos IA",
  "Discos",
  "NTP",
  "Hora",
  "Sistema",
] as const;

export default function DeviceConfigPage() {
  const { id } = useParams();
  const deviceId = Number(id);
  const nav = useNavigate();
  const [device, setDevice] = useState<Device | null>(null);
  const [tab, setTab] = useState<typeof TABS[number]>("Red");

  useEffect(() => {
    getDevice(deviceId).then(setDevice).catch(() => {});
  }, [deviceId]);

  if (!device) return <div className="p-6"><Spinner /></div>;

  return (
    <div className="flex h-full">
      <aside className="w-56 bg-panel border-r border-line p-2 shrink-0">
        <h2 className="text-sm font-semibold mb-2 truncate">{device.name}</h2>
        <p className="text-xs text-muted mb-3 truncate">{device.host}</p>
        <ul className="space-y-1">
          {TABS.map((t) => (
            <li key={t}>
              <button
                onClick={() => setTab(t)}
                className={clsx(
                  "w-full text-left px-2 py-1 rounded text-sm",
                  tab === t ? "bg-accent text-white" : "hover:bg-line",
                )}
              >
                {t}
              </button>
            </li>
          ))}
        </ul>
        <Button variant="ghost" size="sm" className="mt-4 w-full" onClick={() => nav("/devices")}>
          ← Volver
        </Button>
      </aside>
      <section className="flex-1 overflow-auto p-4">
        {tab === "Red" && <NetworkTab deviceId={deviceId} device={device} />}
        {tab === "Canales" && <ChannelsTab deviceId={deviceId} />}
        {tab === "Calidad video" && <EncodeTab deviceId={deviceId} device={device} />}
        {tab === "Snapshot" && <SnapshotTab deviceId={deviceId} device={device} />}
        {tab === "Detección movimiento" && <MotionDetectTab deviceId={deviceId} device={device} />}
        {tab === "Eventos IA" && <SmartEventsTab deviceId={deviceId} device={device} />}
        {tab === "Discos" && <StorageTab deviceId={deviceId} device={device} />}
        {tab === "NTP" && <NtpTab deviceId={deviceId} />}
        {tab === "Hora" && <TimeTab deviceId={deviceId} device={device} />}
        {tab === "Sistema" && <SystemInfoTab device={device} />}
      </section>
    </div>
  );
}

function ChannelsTab({ deviceId }: { deviceId: number }) {
  const [titles, setTitles] = useState<Array<{ channel: number; name: string }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getChannelTitles(deviceId)
      .then((d) => setTitles(d.titles))
      .catch((e) => toast.error(e?.response?.data?.detail || "Error"))
      .finally(() => setLoading(false));
  }, [deviceId]);

  const save = async () => {
    try {
      await patchChannelTitles(deviceId, titles);
      toast.success("Nombres actualizados en el DVR");
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Fallo");
    }
  };

  if (loading) return <Spinner />;
  return (
    <div className="space-y-3 max-w-md">
      <h2 className="text-base font-semibold">Nombres de canales</h2>
      <div className="space-y-1">
        {titles.map((t, i) => (
          <div key={t.channel} className="flex items-center gap-2">
            <span className="text-muted w-12 text-xs">CH{t.channel}</span>
            <input
              className="flex-1 bg-bg border border-line rounded px-2 py-1 text-sm"
              value={t.name}
              onChange={(e) => {
                const next = [...titles];
                next[i] = { ...next[i], name: e.target.value };
                setTitles(next);
              }}
            />
          </div>
        ))}
      </div>
      <Button variant="primary" onClick={save}>Guardar</Button>
    </div>
  );
}

const RESOLUTION_MAP: Record<string, { w: number; h: number; res: string }> = {
  "4K": { w: 3840, h: 2160, res: "3840x2160" },
  "5MP": { w: 2592, h: 1944, res: "2592x1944" },
  "4MP": { w: 2560, h: 1440, res: "2560x1440" },
  "3MP": { w: 2048, h: 1536, res: "2048x1536" },
  "1080P": { w: 1920, h: 1080, res: "1920x1080" },
  "1.3M": { w: 1280, h: 960, res: "1280x960" },
  "1280x960": { w: 1280, h: 960, res: "1280x960" },
  "720P": { w: 1280, h: 720, res: "1280x720" },
  "960H": { w: 928, h: 576, res: "928x576" },
  "D1": { w: 704, h: 480, res: "704x480" },
  "HD1": { w: 352, h: 480, res: "352x480" },
  "BCIF": { w: 704, h: 240, res: "704x240" },
  "2CIF": { w: 704, h: 240, res: "704x240" },
  "CIF": { w: 352, h: 288, res: "352x288" },
  "QCIF": { w: 176, h: 144, res: "176x144" },
  "VGA": { w: 640, h: 480, res: "640x480" },
  "QVGA": { w: 320, h: 240, res: "320x240" },
  "SVCD": { w: 480, h: 480, res: "480x480" },
};

const AUDIO_FREQUENCY_OPTIONS = [
  { value: "8000", label: "8000 Hz" },
  { value: "16000", label: "16000 Hz" },
  { value: "32000", label: "32000 Hz" },
  { value: "44100", label: "44100 Hz" },
  { value: "44000", label: "44000 Hz" },
  { value: "48000", label: "48000 Hz" },
];

function flattenForSetConfig(prefix: string, value: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  const walk = (p: string, v: unknown): void => {
    if (v === null || v === undefined) return;
    if (Array.isArray(v)) { v.forEach((x, i) => walk(`${p}[${i}]`, x)); return; }
    if (typeof v === "object") { Object.entries(v as Record<string, unknown>).forEach(([k, vv]) => walk(p ? `${p}.${k}` : k, vv)); return; }
    if (typeof v === "boolean") { out[p] = v ? "true" : "false"; return; }
    out[p] = String(v);
  };
  walk(prefix, value);
  return out;
}

function getChannelSlot(encode: { Encode?: EncodeChannel[] } | null, channelIdx: number, kind: StreamKind): StreamFormat | null {
  const ch = encode?.Encode?.[channelIdx];
  if (!ch) return null;
  const arr = kind === "Main" ? ch.MainFormat : ch.ExtraFormat;
  return arr?.[0] ?? null;
}

function EncodeTab({ deviceId, device }: { deviceId: number; device: Device }) {
  const [encodeFull, setEncodeFull] = useState<{ Encode?: EncodeChannel[] } | null>(null);
  const [caps, setCaps] = useState<ChannelCaps | null>(null);
  const [capsError, setCapsError] = useState<string | null>(null);
  const [channelIdx, setChannelIdx] = useState(0);
  const [streamKind, setStreamKind] = useState<StreamKind>("Main");
  const [form, setForm] = useState<StreamFormat | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [applyAllOpen, setApplyAllOpen] = useState(false);
  const [channelNames, setChannelNames] = useState<string[]>([]);

  const reload = async () => {
    setLoading(true);
    try {
      const enc = await getEncode(deviceId);
      setEncodeFull(enc);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Error leyendo Encode");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    getChannelTitles(deviceId)
      .then((d) => setChannelNames(d.titles.map((t) => t.name)))
      .catch(() => setChannelNames([]));
  }, [deviceId]);

  useEffect(() => {
    setCapsError(null);
    getEncodeCaps(deviceId, channelIdx + 1)
      .then((resp) => setCaps(parseCapsForChannel(resp, 0)))
      .catch((e) => {
        setCaps(null);
        setCapsError(e?.response?.data?.detail || "No se pudieron leer las capacidades del DVR");
      });
  }, [deviceId, channelIdx]);

  useEffect(() => {
    const slot = getChannelSlot(encodeFull, channelIdx, streamKind);
    if (slot) setForm(JSON.parse(JSON.stringify(slot)));
  }, [encodeFull, channelIdx, streamKind]);

  if (loading || !encodeFull) return <Spinner />;

  const totalChannels = encodeFull.Encode?.length || device.channels_count || 1;
  const streamCaps = streamKind === "Main" ? caps?.main : caps?.extra;
  const channelOptions = Array.from({ length: totalChannels }, (_, i) => ({
    value: String(i),
    label: channelNames[i] ? `CH${i + 1} - ${channelNames[i]}` : `CH${i + 1}`,
  }));

  const updateVideo = (patch: Partial<VideoFormat>) => {
    setForm((prev) => prev ? { ...prev, Video: { ...prev.Video, ...patch } } : prev);
  };

  const handleResolutionChange = (name: string) => {
    const m = RESOLUTION_MAP[name];
    if (m) {
      updateVideo({ CustomResolutionName: name, resolution: m.res, Width: m.w, Height: m.h });
    } else {
      updateVideo({ CustomResolutionName: name });
    }
  };

  const buildPayloadForChannel = (idx: number, slot: StreamFormat): Record<string, string> => {
    const prefix = `Encode[${idx}].${streamKind}Format[0]`;
    return flattenForSetConfig(prefix, slot);
  };

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const payload = buildPayloadForChannel(channelIdx, form);
      await patchEncode(deviceId, payload);
      toast.success(`Canal ${channelIdx + 1} actualizado`);
      await reload();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Fallo al guardar");
    } finally {
      setSaving(false);
    }
  };

  const applyToAll = async () => {
    if (!form) return;
    setApplyAllOpen(false);
    setSaving(true);
    try {
      const payload: Record<string, string> = {};
      for (let i = 0; i < totalChannels; i++) {
        Object.assign(payload, buildPayloadForChannel(i, form));
      }
      await patchEncode(deviceId, payload);
      toast.success(`Aplicado a ${totalChannels} canales`);
      await reload();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Fallo aplicando a todos");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    await reload();
    toast.success("Restablecido desde el DVR");
  };

  return (
    <div className="space-y-4 max-w-4xl">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-base font-semibold">Calidad de video</h2>
          <p className="text-xs text-muted mt-0.5">
            Para evitar transcodificacion del live view, el codec debe ser H.264.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <Select
            label="Canal"
            options={channelOptions}
            value={String(channelIdx)}
            onChange={(e) => setChannelIdx(Number(e.target.value))}
            className="min-w-[200px]"
          />
          <Button variant="secondary" onClick={() => setApplyAllOpen(true)} disabled={saving || !form}>
            Aplicar a todos los canales
          </Button>
        </div>
      </header>

      <div className="flex gap-1 border-b border-line">
        {(["Main", "Extra"] as const).map((k) => (
          <button
            key={k}
            onClick={() => setStreamKind(k)}
            className={clsx(
              "px-3 py-1.5 text-sm border-b-2 -mb-px",
              streamKind === k ? "border-accent text-text" : "border-transparent text-muted hover:text-text",
            )}
          >
            {k === "Main" ? "Stream principal" : "Sub-stream"}
          </button>
        ))}
      </div>

      {capsError && (
        <div className="bg-yellow-900/30 border border-yellow-700 text-yellow-200 text-xs rounded p-2">
          {capsError}. Los dropdowns mostraran las opciones del config actual; usa el editor JSON para cambios fuera de rango.
        </div>
      )}

      {!form ? (
        <div className="text-sm text-muted">Sin datos para este canal/stream.</div>
      ) : (
        <>
          <VideoSection
            video={form.Video}
            caps={streamCaps ?? null}
            onChange={updateVideo}
            onResolutionChange={handleResolutionChange}
          />
          <AudioSection
            slot={form}
            caps={streamCaps ?? null}
            onChange={(patch) => setForm((prev) => prev ? { ...prev, ...patch } : prev)}
            onAudioVideoFieldChange={(patch) => setForm((prev) => prev ? { ...prev, Audio: { ...prev.Audio, ...patch } } : prev)}
          />
          <div className="flex gap-2">
            <Button variant="primary" onClick={save} loading={saving}>Guardar</Button>
            <Button variant="ghost" onClick={reset} disabled={saving}>Restablecer</Button>
          </div>
        </>
      )}

      <details className="mt-4">
        <summary className="text-xs text-muted cursor-pointer">Modo avanzado (JSON crudo)</summary>
        <div className="mt-2">
          <p className="text-xs text-muted mb-2">
            Editar via JSON crudo usando los keys de Dahua, ej: <code>Encode[0].MainFormat[0].Video.FPS=25</code>.
          </p>
          <RawJsonEditor
            data={encodeFull}
            onSave={async (obj) => {
              const flat = flattenForSetConfig("", obj);
              await patchEncode(deviceId, flat);
              toast.success("Encode actualizado");
              await reload();
            }}
          />
        </div>
      </details>

      <ConfirmDialog
        open={applyAllOpen}
        title="Aplicar a todos los canales"
        message={`Esta accion va a sobrescribir la calidad del ${streamKind === "Main" ? "stream principal" : "sub-stream"} en los ${totalChannels} canales con la configuracion actual. ¿Continuar?`}
        confirmText="Aplicar a todos"
        destructive
        onConfirm={applyToAll}
        onCancel={() => setApplyAllOpen(false)}
      />
    </div>
  );
}

function VideoSection({
  video, caps, onChange, onResolutionChange,
}: {
  video: VideoFormat;
  caps: ChannelCaps["main"] | null;
  onChange: (patch: Partial<VideoFormat>) => void;
  onResolutionChange: (name: string) => void;
}) {
  const resolutionOpts = (caps?.resolutionTypes.length ? caps.resolutionTypes : [video.CustomResolutionName || "1080P"]).map((r) => ({ value: r, label: r }));
  const compressionOpts = (caps?.compressionTypes.length ? caps.compressionTypes : ["H.264", "H.265", "MJPG"]).map((c) => ({ value: c, label: c }));
  const profileOpts = ((): { value: string; label: string }[] => {
    if (video.Compression === "H.265") return [{ value: "Main", label: "Main" }, { value: "Main10", label: "Main10" }];
    const list = caps?.h264Profiles.length ? caps.h264Profiles : ["Baseline", "Main", "High"];
    return list.map((p) => ({ value: p, label: p }));
  })();
  const bitRateMin = caps?.bitRateMin || 32;
  const bitRateMax = caps?.bitRateMax || 8192;
  const fpsMax = caps?.fpsMax || 30;
  const qualityRange = video.QualityRange || 6;
  const isVbr = String(video.BitRateControl).toUpperCase() === "VBR";

  return (
    <section className="border border-line rounded p-3 space-y-3 bg-panel/30">
      <h3 className="text-sm font-semibold">Video</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Select
          label="Resolucion"
          options={resolutionOpts}
          value={video.CustomResolutionName || ""}
          onChange={(e) => onResolutionChange(e.target.value)}
        />
        <Select
          label="Compresion"
          options={compressionOpts}
          value={video.Compression}
          onChange={(e) => onChange({ Compression: e.target.value })}
        />
        <Select
          label="Control de bitrate"
          options={[{ value: "CBR", label: "CBR (constante)" }, { value: "VBR", label: "VBR (variable)" }]}
          value={String(video.BitRateControl || "CBR").toUpperCase()}
          onChange={(e) => onChange({ BitRateControl: e.target.value })}
        />
        <Input
          label={`Bitrate (kbps) ${bitRateMin}-${bitRateMax}`}
          type="number"
          min={bitRateMin}
          max={bitRateMax}
          value={String(video.BitRate ?? "")}
          onChange={(e) => onChange({ BitRate: Number(e.target.value) })}
        />
        <Input
          label={`FPS (max ${fpsMax})`}
          type="number"
          min={1}
          max={fpsMax}
          value={String(video.FPS ?? "")}
          onChange={(e) => onChange({ FPS: Number(e.target.value) })}
        />
        <Input
          label="GOP (1-150)"
          type="number"
          min={1}
          max={150}
          value={String(video.GOP ?? "")}
          onChange={(e) => onChange({ GOP: Number(e.target.value) })}
        />
        <Input
          label={`Calidad (1-${qualityRange}, solo VBR)`}
          type="number"
          min={1}
          max={qualityRange}
          disabled={!isVbr}
          value={String(video.Quality ?? "")}
          onChange={(e) => onChange({ Quality: Number(e.target.value) })}
        />
        <Select
          label="Profile"
          options={profileOpts}
          value={String(video.Profile || "")}
          onChange={(e) => onChange({ Profile: e.target.value })}
        />
        {typeof video.SmartEnable === "boolean" && (
          <label className="flex items-center gap-2 text-xs mt-5">
            <input
              type="checkbox"
              checked={!!video.SmartEnable}
              onChange={(e) => onChange({ SmartEnable: e.target.checked })}
              className="bg-bg border border-line rounded"
            />
            <span>Smart codec (H.264+/H.265+)</span>
          </label>
        )}
      </div>
    </section>
  );
}

function AudioSection({
  slot, caps, onChange, onAudioVideoFieldChange,
}: {
  slot: StreamFormat;
  caps: ChannelCaps["main"] | null;
  onChange: (patch: Partial<StreamFormat>) => void;
  onAudioVideoFieldChange: (patch: Record<string, unknown>) => void;
}) {
  const audio = slot.Audio || ({} as StreamFormat["Audio"]);
  const compOpts = (caps?.audioCompressionTypes.length ? caps.audioCompressionTypes : ["G.711A", "G.711Mu", "PCM", "AAC", "G.726"]).map((c) => ({ value: c, label: c }));

  return (
    <section className="border border-line rounded p-3 space-y-3 bg-panel/30">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Audio</h3>
        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={!!slot.AudioEnable}
            onChange={(e) => onChange({ AudioEnable: e.target.checked })}
          />
          <span>Habilitar audio</span>
        </label>
      </div>
      <div className={clsx("grid grid-cols-2 md:grid-cols-3 gap-3", !slot.AudioEnable && "opacity-50 pointer-events-none")}>
        <Select
          label="Compresion"
          options={compOpts}
          value={String(audio.Compression || "")}
          onChange={(e) => onAudioVideoFieldChange({ Compression: e.target.value })}
        />
        <Select
          label="Frecuencia"
          options={AUDIO_FREQUENCY_OPTIONS}
          value={String(audio.Frequency || "")}
          onChange={(e) => onAudioVideoFieldChange({ Frequency: Number(e.target.value) })}
        />
        <Input
          label="Bitrate (kbps)"
          type="number"
          value={String(audio.Bitrate ?? "")}
          onChange={(e) => onAudioVideoFieldChange({ Bitrate: Number(e.target.value) })}
        />
      </div>
    </section>
  );
}

function NtpTab({ deviceId }: { deviceId: number }) {
  const [data, setData] = useState<any>(null);
  const [addr, setAddr] = useState("");
  useEffect(() => {
    getNtp(deviceId).then((d) => {
      setData(d);
      const a = d?.NTP?.Address || d?.table?.NTP?.Address || "";
      setAddr(String(a));
    }).catch((e) => toast.error(e?.response?.data?.detail || "Error"));
  }, [deviceId]);
  const save = async () => {
    try {
      await patchNtp(deviceId, { "NTP.Address": addr });
      toast.success("NTP actualizado");
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Fallo"); }
  };
  return (
    <div className="space-y-3 max-w-md">
      <h2 className="text-base font-semibold">NTP</h2>
      <Input label="Servidor NTP" value={addr} onChange={(e) => setAddr(e.target.value)} placeholder="pool.ntp.org" />
      <Button variant="primary" onClick={save}>Guardar</Button>
      {data && <details className="mt-2"><summary className="text-xs text-muted cursor-pointer">JSON</summary><pre className="text-xs bg-panel p-2 rounded mt-1">{JSON.stringify(data, null, 2)}</pre></details>}
    </div>
  );
}

function RawJsonEditor({ data, onSave }: { data: any; onSave: (obj: any) => Promise<void> }) {
  const [text, setText] = useState(JSON.stringify(data, null, 2));
  const save = async () => {
    let obj: any;
    try { obj = JSON.parse(text); } catch { toast.error("JSON invalido"); return; }
    await onSave(obj);
  };
  return (
    <div>
      <textarea
        className="w-full h-72 bg-bg border border-line rounded p-2 font-mono text-xs"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <Button variant="primary" onClick={save}>Guardar</Button>
    </div>
  );
}
